import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Tags } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

/** Weekly "these apps still need classifying" nudge, with a live preview of
 *  exactly what the next email would say. */
export function ProdAppNudgeCard({ canManage }) {
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [recip, setRecip] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/productivity/apps/nudge").then(({ data: d }) => {
    setData(d);
    setCfg({ ...d.config });
    setRecip((d.config.recipients || []).join(", "));
  }).catch((e) => toast.error(e.response?.data?.detail || "Could not load the nudge"));

  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/productivity/apps/nudge", {
        enabled: !!cfg.enabled,
        min_minutes: Number(cfg.min_minutes),
        lookback_days: Number(cfg.lookback_days),
        max_items: Number(cfg.max_items),
        ai_suggest: cfg.ai_suggest !== false,
        recipients: recip,
      });
      setData(d);
      setCfg({ ...d.config });
      setRecip((d.config.recipients || []).join(", "));
      toast.success("Nudge saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/productivity/apps/nudge/send-now");
      if (r.status === "nothing_to_flag") toast.success("Nothing to nudge about — everything is classified");
      else if (r.status === "sent") toast.success(`Nudge sent — ${r.count} app(s) listed`);
      else if (r.status === "paused") toast.warning("Outbound email is paused — nothing delivered");
      else toast.error(`Status: ${r.status}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  if (!cfg) return <Card className="p-4"><Loader2 className="h-4 w-4 animate-spin text-slate-300 dark:text-slate-500" /></Card>;
  const p = data?.preview;

  return (
    <Card className="p-4" data-testid="prod-settings-nudge">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tags className="h-4 w-4 text-primary" />
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">Unclassified apps nudge</h3>
        </div>
        <Switch checked={!!cfg.enabled} disabled={!canManage} data-testid="prod-nudge-enabled"
          onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled: v }))} />
      </div>
      <p className="mb-4 text-xs text-slate-500 dark:text-slate-400">
        Emailed to managers every Monday 08:20 America/Edmonton, listing apps and websites nobody has
        classified yet so the Applications list never drifts out of date. Skipped entirely when
        there&apos;s nothing to report.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          Ignore anything under (minutes)          <Input type="number" min="1" max="600" value={cfg.min_minutes} disabled={!canManage}
            data-testid="prod-nudge-min-minutes" className="mt-1 h-8"
            onChange={(e) => setCfg((c) => ({ ...c, min_minutes: e.target.value }))} />
        </label>
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          Look back (days)
          <Input type="number" min="1" max="60" value={cfg.lookback_days} disabled={!canManage}
            data-testid="prod-nudge-lookback" className="mt-1 h-8"
            onChange={(e) => setCfg((c) => ({ ...c, lookback_days: e.target.value }))} />
        </label>
        <label className="block text-xs text-slate-600 dark:text-slate-300">
          List at most
          <Input type="number" min="5" max="100" value={cfg.max_items} disabled={!canManage}
            data-testid="prod-nudge-max" className="mt-1 h-8"
            onChange={(e) => setCfg((c) => ({ ...c, max_items: e.target.value }))} />
        </label>
      </div>

      <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
        <div>
          <div className="text-xs font-medium text-slate-700 dark:text-slate-200">Pre-guess each app with AI</div>
          <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
            The email shows a suggested classification with a <b>confirm ✓</b> link beside it, plus an
            “accept all” button — so sorting the list is one click. Links are signed, expire after 14
            days, and never overwrite a classification someone has already made.
          </p>
        </div>
        <Switch checked={cfg.ai_suggest !== false} disabled={!canManage} data-testid="prod-nudge-ai"
          onCheckedChange={(v) => setCfg((c) => ({ ...c, ai_suggest: v }))} />
      </div>

      {p && (
        <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-3" data-testid="prod-nudge-preview">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            What the next nudge would say
          </div>
          {p.count === 0 ? (
            <p className="text-sm text-emerald-700" data-testid="prod-nudge-preview-clear">
              Nothing outstanding — every app over {p.min_minutes} minutes is classified.
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-700 dark:text-slate-200">
                <b>{p.count}</b> app(s)/website(s) totalling <b>{p.hms}</b> aren&apos;t classified
                {p.new_count > 0 && <> — <b>{p.new_count}</b> appeared for the first time</>}.
              </p>
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {p.items.map((i) => (
                  <div key={i.name} className="flex items-center gap-2 text-xs"
                    data-testid={`prod-nudge-item-${i.name}`}>
                    <span className="font-mono text-slate-700 dark:text-slate-200">{i.name}</span>
                    {i.is_new && <span className="rounded bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-700">new</span>}
                    {i.suggested_cls && (
                      <span data-testid={`prod-nudge-guess-${i.name}`}
                        className={`rounded px-1.5 text-[10px] font-semibold ${
                          i.suggested_cls === "productive" ? "bg-emerald-100 text-emerald-700"
                            : i.suggested_cls === "unproductive" ? "bg-rose-100 text-rose-700"
                              : "bg-slate-200 text-slate-600 dark:text-slate-300"}`}>
                        {i.suggested_cls}
                      </span>
                    )}
                    <span className="ml-auto text-slate-500 dark:text-slate-400">{i.hms}</span>
                    <span className="w-16 text-right text-slate-400 dark:text-slate-400">{i.users} {i.users === 1 ? "person" : "people"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <label className="mt-4 block text-xs text-slate-600 dark:text-slate-300">
        Who gets this email
        <Textarea value={recip} onChange={(e) => setRecip(e.target.value)} rows={2} disabled={!canManage}
          data-testid="prod-nudge-recipients" placeholder="reo@unitedcloud.ca, keith@unitedcloud.ca"
          className="mt-1 text-sm" />
      </label>
      <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
        One or more email addresses, separated by commas.
        {!!(data?.config?.recipients || []).length && (
          <span data-testid="prod-nudge-recipients-current"> Currently going to{" "}
            {data.config.recipients.join(", ")}.</span>
        )}
      </p>
      {!!(data?.config?.blocked_recipients || []).length && (
        <p className="mt-1 text-[11px] text-rose-600" data-testid="prod-nudge-recipients-blocked">
          Not delivered to {data.config.blocked_recipients.join(", ")} — they are monitored in these
          reports.
        </p>
      )}
      <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
        Anyone listed under Monitored users can&apos;t receive it — nudges go to managers only.
      </p>
      {data?.last_send && (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400" data-testid="prod-nudge-last-send">
          Last send: {data.last_send.sent_at?.slice(0, 16).replace("T", " ")} UTC ·{" "}
          {data.last_send.status} · {data.last_send.count || 0} app(s)
        </div>
      )}
      {canManage && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={sendNow} disabled={busy} data-testid="prod-nudge-send-now">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}
            Send now
          </Button>
          <Button size="sm" onClick={save} disabled={busy} data-testid="prod-nudge-save">Save</Button>
        </div>
      )}
    </Card>
  );
}
